import { Card, CardContent, Dialog, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { DeleteButton } from '../../../components/delete-button.component';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useFormContext, useWatch } from 'react-hook-form';
import { Icons } from '../../../assets/icons';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { useEffect, useRef, useState } from 'react';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
// ✅ Import the NEW helper — same one used in TimeAndMaterialEditForm
import { getAddressCoreAndType } from '../../../utils/abbreviations.util';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { set } from 'date-fns';
import { ProjectType } from '../../../models/project';

const lotTypeOption = [
  { value: 'TH', label: 'Townhome (TH)' },
  { value: 'SFR', label: 'Single-Family Residential (SFR)' },
];

const lotTypeCMOption = [{ value: 'CM', label: 'Commercial (CM)' }];

const getBaseJobSite = (jobsite: string, unitName: string): string => {
  const js = jobsite || '';
  const un = (unitName || '').trim();
  if (un && js.trim().endsWith(un)) {
    return js.slice(0, js.lastIndexOf(un));
  }
  return js;
};

interface BuildingFloorUnitFormProps {
  name: string;
  index?: number;
  onRemove?: (index: number) => void;
  isEnabled?: boolean;
  page?: string;
  syncWithDux: any;
  projectTypeValue: any;
  setAllBuildingLotsData: (value: any) => void;
  projectStreet?: string;
  projectCommunityType?: string;
  floorNo?: number | string;
}

export const BuildingFloorUnitForm = ({
  name,
  index,
  onRemove,
  isEnabled = false,
  page,
  syncWithDux,
  projectTypeValue,
  setAllBuildingLotsData,
  projectStreet,
  projectCommunityType,
  floorNo,
}: BuildingFloorUnitFormProps) => {
  const { getValues, setValue, watch, setError, clearErrors, formState, getFieldState } = useFormContext();
  const navigate = useNavigate();
  const { data, isLoading } = useLoadData<ApiResponse<any>>(
    projectsApiKeyStore.generateLotName(getValues('projectID')),
    isEnabled
  );

  const jobsiteRef = useRef<string | undefined>(
    getBaseJobSite(getValues(`${name}.jobsite`) || '', getValues(`${name}.unitName`) || '')
  );

  // ── Lot type tracking refs ────────────────────────────────────────────────
  const previousLotType = useRef<string | null>(getValues(`${name}.lotType`) ?? null);
  const originalLotType = useRef<string | null>(getValues(`${name}.lotType`) ?? null);
  const lotTypeChangedRef = useRef<boolean>(false);

  // ✅ Track latest typed address value so the polling interval can detect stale errors
  const latestAddressValue = useRef<string>('');

  const [jobNumberWarning, setJobNumberWarning] = useState<string>('');
  const [lotTypeChangeDialog, setLotTypeChangeDialog] = useState<{
    open: boolean;
    newValue: string;
    previousValue: string;
  }>({ open: false, newValue: '', previousValue: '' });

  useWatch({ name: `${name}.unitName` || [] });

  useEffect(() => {
    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );
    setAllBuildingLotsData(mergedLots);
  }, [getValues('projectBuildings')]);

  useEffect(() => {
    if (projectTypeValue && projectTypeValue !== 'RES') {
      setValue(`${name}.lotType`, 'CM');
      if (projectCommunityType === ProjectType.Buildings && !getValues(`${name}.address`)) {
        setValue(`${name}.address`, projectStreet);
      }
      if (floorNo && !getValues(`${name}.unitName`)) {
        setValue(`${name}.unitName`, `${floorNo}-`);
      }
    }
  }, [projectTypeValue]);

  // ── normalize helper ──────────────────────────────────────────────────────
  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  // ✅ Poll every 300ms to clear stale API-set errors.
  // Uses getFieldState (resolves nested paths into the live RHF store) instead of
  // formState.errors flat-key lookup, which doesn't work for deep paths like
  // "projectBuildings.0.buildingFloors.0.buildingLots.0".
  useEffect(() => {
    const interval = setInterval(() => {
      // ── Clear stale server-type Lot/Unit name errors ─────────────────────
      // After a swap the DB still holds the old value, so the API returns
      // "already exists" even though form values are now locally unique.
      if (getFieldState(`${name}.unitName`)?.error?.type === 'server') {
        const currentUnitName = getValues(`${name}.unitName`) ?? '';
        if (currentUnitName) {
          const allLots = getAllUniqueLots();
          const allUnitNames = allLots.map((lot: any) => normalize(lot.unitName));
          const isDuplicate = allUnitNames.filter((n: string) => n === normalize(currentUnitName)).length > 1;
          if (!isDuplicate) {
            clearErrors(`${name}.unitName`);
          }
        }
      }

      // ── Clear stale address errors ────────────────────────────────────────
      const currentErrorMsg = getFieldState(`${name}.address`)?.error?.message;
      if (!currentErrorMsg) return;

      const currentFieldValue = getValues(`${name}.address`) ?? '';
      const trackedValue = latestAddressValue.current ?? '';
      const allLots = getAllUniqueLots();

      if (currentErrorMsg === 'Address already exists.') {
        const allAddresses = allLots.map((lot: any) => normalize(lot.address));
        const isDuplicate = allAddresses.filter((a: string) => a === normalize(currentFieldValue)).length > 1;
        if (!isDuplicate || normalize(currentFieldValue) !== normalize(trackedValue)) {
          clearErrors(`${name}.address`);
        }
      }

      if (currentErrorMsg === 'Address matches an existing address after abbreviation standardization.') {
        const { core: currentCore, streetType: currentStreetType } = getAddressCoreAndType(currentFieldValue);
        if (currentStreetType === null) {
          clearErrors(`${name}.address`);
          return;
        }
        const otherLots = allLots.filter((lot: any) => normalize(lot.address) !== normalize(currentFieldValue));
        const isCoreAndTypeMatch = otherLots.some((lot: any) => {
          const { core: otherCore, streetType: otherStreetType } = getAddressCoreAndType(lot?.address ?? '');
          return otherCore === currentCore && otherStreetType === currentStreetType;
        });
        if (!isCoreAndTypeMatch) {
          clearErrors(`${name}.address`);
        }
      }
    }, 300);

    return () => clearInterval(interval);
  }, [name]); // getFieldState reads live state on every tick — no formState.errors needed

  // ── Helper: get all unique lots across buildings/floors ───────────────────
  const getAllUniqueLots = () => {
    const mergedLots =
      getValues('projectBuildings')?.flatMap((building: any) =>
        building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
      ) ?? [];
    return mergedLots.filter(
      (lot: any, i: number, self: any) => i === self.findIndex((l: any) => l.unitID === lot.unitID)
    );
  };

  // ── Clears stale duplicate errors on every sibling lot after any field change ─
  const syncSiblingLotErrors = () => {
    const buildings = getValues('projectBuildings') ?? [];
    const siblings: Array<{ basePath: string; unitName: string; address: string }> = [];
    const allUnitNames: string[] = [];
    const allAddresses: string[] = [];

    buildings.forEach((building: any, bi: number) => {
      (building?.buildingFloors ?? []).forEach((floor: any, fi: number) => {
        (floor?.buildingLots ?? []).forEach((lot: any, li: number) => {
          const basePath = `projectBuildings.${bi}.buildingFloors.${fi}.buildingLots.${li}`;
          const unitName = normalize(lot?.unitName ?? '');
          const address = normalize(lot?.address ?? '');
          if (unitName) allUnitNames.push(unitName);
          if (address) allAddresses.push(address);
          if (basePath !== name) siblings.push({ basePath, unitName, address });
        });
      });
    });

    siblings.forEach(({ basePath, unitName, address }) => {
      if (unitName && allUnitNames.filter((n) => n === unitName).length <= 1) {
        clearErrors(`${basePath}.unitName`);
      }
      if (address && allAddresses.filter((a) => a === address).length <= 1) {
        clearErrors(`${basePath}.address`);
      }
    });
  };

  const handleUnitNameChange = (value: string) => {
    const prefix = floorNo ? `${floorNo}-` : '';
    if (prefix && !value.startsWith(prefix)) {
      setValue(`${name}.unitName`, prefix);
      return;
    }

    let initialJobNumber = jobsiteRef.current || getValues(`${name}.jobsite`);
    if (!initialJobNumber) initialJobNumber = '';
    const newJobNumber = `${initialJobNumber}${value}`.trim();
    setValue(`${name}.jobsite`, newJobNumber);

    if (!value || value?.trim() === '') {
      clearErrors(`${name}.unitName`);
      syncSiblingLotErrors();
      return;
    }

    const uniqueLots = getAllUniqueLots();
    const allUnits = uniqueLots?.map((u: any) => normalize(u?.unitName));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      setError(`${name}.unitName`, { type: 'manual', message: 'Lot/Unit name already exists.' });
    } else {
      clearErrors(`${name}.unitName`);
    }
    syncSiblingLotErrors();
  };

  const handleJobnameNameChange = (value: any) => {
    jobsiteRef.current = value;

    if (!value || value?.trim() === '') {
      clearErrors(`${name}.jobsite`);
      return;
    }

    const uniqueLots = getAllUniqueLots();
    const allUnits = uniqueLots?.map((u: any) => normalize(u?.jobsite));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      setError(`${name}.jobsite`, { type: 'manual', message: 'Jobsite already exists.' });
    } else {
      clearErrors(`${name}.jobsite`);
    }
  };

  const handleJobNumberChange = (value: any) => {
    if (!value || value?.trim() === '') {
      setJobNumberWarning('');
      return;
    }

    const uniqueLots = getAllUniqueLots();
    const allUnits = uniqueLots?.map((u: any) => normalize(u?.jobNumber));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    setJobNumberWarning(isDuplicate ? 'Job number already exists.' : '');
  };

  // ✅ handleAddressChange: fully ported from TimeAndMaterialEditForm
  //    Uses getAddressCoreAndType for abbreviation check (not the old getAddressAbbreviationError)
  const handleAddressChange = (value: any) => {
    // Always track latest typed value so the polling interval detects stale errors
    latestAddressValue.current = value ?? '';

    // Always clear first — removes stale errors from the previous value
    clearErrors(`${name}.address`);

    if (!value || value?.trim() === '') return;

    const allLots = getAllUniqueLots();

    // ── 1. Exact duplicate check ─────────────────────────────────────────────
    const allAddressesNormalized = allLots.map((u: any) => normalize(u?.address));
    const isExactDuplicate = allAddressesNormalized.filter((a: string) => a === normalize(value)).length > 1;

    if (isExactDuplicate) {
      setError(`${name}.address`, {
        type: 'manual',
        message: 'Address already exists.',
      });
      return;
    }

    // ── 2. Abbreviation-standardization duplicate check ──────────────────────
    // Only flags when:
    //   a) The entered address ends with a known street-type suffix
    //   b) Another lot has the SAME core AND the SAME canonical street type
    //
    // "3055 TOTEM ROAD" vs "3055 TOTEM RD"  → core match + both "Road"  → FLAG ✅
    // "3055 TOTEM ROAD" vs "3055 TOTEM ST"  → core match + Road vs Street → NO FLAG ✅
    const { core: currentCore, streetType: currentStreetType } = getAddressCoreAndType(value);

    if (currentStreetType !== null && currentCore !== '') {
      // Exclude self from comparison (match by normalized address since unitID may be 0 for new rows)
      const otherLots = allLots.filter((lot: any) => normalize(lot?.address ?? '') !== normalize(value));

      const isCoreAndTypeMatch = otherLots.some((lot: any) => {
        const { core: otherCore, streetType: otherStreetType } = getAddressCoreAndType(lot?.address ?? '');
        return (
          otherCore === currentCore && // same non-type prefix
          otherStreetType === currentStreetType // same canonical street type (e.g. both "Road")
        );
      });

      if (isCoreAndTypeMatch) {
        setError(`${name}.address`, {
          type: 'manual',
          message: 'Address matches an existing address after abbreviation standardization.',
        });
      }
    }

    syncSiblingLotErrors();
  };

  // ── Lot type change handler ───────────────────────────────────────────────
  const handleLotTypeChange = (newValue: string) => {
    const prevValue = previousLotType.current;
    const origValue = originalLotType.current;

    const isSameAsPrevious = prevValue === newValue;
    const isRevertedToOriginal = !!origValue && origValue === newValue;
    const isUnitCreationCompleted = getValues(`${name}.isUnitCreationCompleted`);

    if (isSameAsPrevious) return;

    if (!isUnitCreationCompleted || !origValue || isRevertedToOriginal) {
      lotTypeChangedRef.current = false;
      previousLotType.current = newValue;
      setValue(`${name}.lotType`, newValue);
      if (isRevertedToOriginal) {
        setValue(`${name}.lotTypeChanged`, false);
        setValue(`${name}.isApplyAppropriatePLConfirmed`, false);
      }
    } else {
      lotTypeChangedRef.current = true;
      setLotTypeChangeDialog({ open: true, newValue, previousValue: prevValue as string });
    }
  };

  const handleLotTypeConfirmYes = () => {
    const { newValue } = lotTypeChangeDialog;
    setValue(`${name}.lotType`, newValue);
    setValue(`${name}.isApplyAppropriatePLConfirmed`, true);
    setValue(`${name}.lotTypeChanged`, true);
    previousLotType.current = newValue;
    setLotTypeChangeDialog({ open: false, newValue: '', previousValue: '' });
  };

  const handleLotTypeConfirmNo = () => {
    const { newValue } = lotTypeChangeDialog;
    setValue(`${name}.lotType`, newValue);
    setValue(`${name}.isApplyAppropriatePLConfirmed`, false);
    setValue(`${name}.lotTypeChanged`, true);
    previousLotType.current = newValue;
    setLotTypeChangeDialog({ open: false, newValue: '', previousValue: '' });
  };

  const projectSyncWithDUX = watch(`${name}.projectSyncWithDUX`);
  const isProjectTypeRES = true;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Card elevation={0} className="floorUnitBlockWrapper" style={{ marginBottom: '0', background: 'none' }}>
        <CardContent style={{ paddingTop: '4px', paddingBottom: '4px', paddingLeft: '4px' }}>
          <Grid container spacing={2}>
            <Grid item lg={8} sm={8} xl={8} xs={8}>
              <div style={{ display: 'none' }}>
                <label>{getValues(`${name}.unitNumber`)}</label>
              </div>
              <Grid container spacing={2}>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.8 : 2}
                  sm={isProjectTypeRES ? 1.8 : 2}
                  xl={isProjectTypeRES ? 1.8 : 2}
                  xs={isProjectTypeRES ? 1.8 : 2}
                >
                  <FormInput
                    name={`${name}.jobsite`}
                    label={'Jobsite'}
                    onChange={(e: any) => handleJobnameNameChange(e)}
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                        getValues('projectID'),
                        '',
                        getValues(`${name}.unitID`),
                        '',
                        value
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.7 : 2}
                  sm={isProjectTypeRES ? 1.7 : 2}
                  xl={isProjectTypeRES ? 1.7 : 2}
                  xs={isProjectTypeRES ? 1.7 : 2}
                >
                  <FormInput
                    name={`${name}.unitName`}
                    label="Lot/Unit"
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressUnitName(
                        getValues('projectID'),
                        '',
                        getValues(`${name}.unitID`),
                        value,
                        ''
                      )
                    }
                    onChange={(e: any) => handleUnitNameChange(e)}
                  />
                </Grid>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                  <FormInput
                    name={`${name}.jobNumber`}
                    label={'Builder Job Number'}
                    onChange={(e: any) => handleJobNumberChange(e)}
                  />
                  {jobNumberWarning && (
                    <Typography variant="caption" style={{ color: '#e67e22', fontSize: '11px' }}>
                      {jobNumberWarning}
                    </Typography>
                  )}
                </Grid>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.5 : 2}
                  sm={isProjectTypeRES ? 1.5 : 2}
                  xl={isProjectTypeRES ? 1.5 : 2}
                  xs={isProjectTypeRES ? 1.5 : 2}
                >
                  <FormInput
                    name={`${name}.address`}
                    label="Address"
                    onChange={(e: any) => handleAddressChange(e)}
                    isCustomError={true}
                    validationQueryRoute={(value: string) =>
                      projectsApiKeyStore.checkDuplicateLotAddressOfAddress(
                        getValues('projectID'),
                        value,
                        getValues(`${name}.unitID`),
                        '',
                        ''
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  lg={isProjectTypeRES ? 1.5 : 2}
                  sm={isProjectTypeRES ? 1.5 : 2}
                  xl={isProjectTypeRES ? 1.5 : 2}
                  xs={isProjectTypeRES ? 1.5 : 2}
                >
                  <FormInput name={`${name}.squareFeet`} label={'Square Feet'} />
                </Grid>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                  <FormAutoCompleteSelectDynamic
                    name={`${name}.proctorResult`}
                    label="Proctor Result"
                    getQueryKeyConfig={() => proctorResultApiKeyStore.getProjectProctorResults(getValues('projectID'))}
                    optionsValueKey="proctorResultsID"
                    optionsLabelKey="proctorFileName"
                  />
                </Grid>
                {isProjectTypeRES && (
                  <Grid item lg={1.5} sm={1.5} xl={1.5} xs={1.5}>
                    <FormSelect
                      name={`${name}.lotType`}
                      label="Lot Type"
                      options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                      error={true}
                      onChange={(e: any) => handleLotTypeChange(e)}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item lg={4} sm={4} xl={4} xs={4}>
              <Grid container direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                <FormCheckbox label="Create WO" name={`${name}.createWO`} />
                <FormCheckbox label="Sync With DUX" name={`${name}.syncWithDUX`} disabled={!projectSyncWithDUX} />
                <FormInput label="Building Permit#" name={`${name}.buildingPermit`} styleField={{ width: '200px' }} />
                {onRemove && index !== undefined && (
                  <div>
                    {page && getValues(`${name}.isUnitCreationCompleted`) && !!getValues(`${name}.unitID`) && (
                      <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                        <IconButton
                          style={{ padding: '10px 2px 5px 15px' }}
                          aria-label="view"
                          onClick={() => navigate(`/units/profile/${getValues(`${name}.unitID`)}`)}
                        >
                          <Tooltip title={<Typography variant="body1">View</Typography>}>
                            <img src={Icons.IconUnitsLotsDetails} alt="View" />
                          </Tooltip>
                        </IconButton>
                      </GuardedComponent>
                    )}
                  </div>
                )}
                {!!getValues(`${name}.unitID`) && (
                  <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                    {onRemove &&
                      index !== undefined &&
                      (!getValues(`${name}.disableDelete`) ? (
                        <div>
                          <DeleteButton
                            data={undefined}
                            deleteMutationConfig={() => projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))}
                            onDeleted={() => onRemove(index)}
                          >
                            <>
                              <Tooltip title={<Typography variant="body1">DELETE</Typography>}>
                                <img src={Icons.Delete} alt="DELETE" />
                              </Tooltip>
                            </>
                          </DeleteButton>
                        </div>
                      ) : (
                        <div>
                          <DeleteButton
                            data={undefined}
                            deleteMutationConfig={() => projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))}
                            onDeleted={() => onRemove(index)}
                            isDisabled={true}
                          >
                            <>
                              <Tooltip title={<Typography variant="body1">DELETE</Typography>}>
                                <img src={Icons.DisabledDelete} alt="DELETE" />
                              </Tooltip>
                            </>
                          </DeleteButton>
                        </div>
                      ))}
                  </GuardedComponent>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {lotTypeChangeDialog.open && (
        <Dialog open={lotTypeChangeDialog.open} className="modalpopup">
          <div style={{ padding: '25px 50px', textAlign: 'center' }}>
            <Typography variant="body1">
              Do you want to update the pricing for this lot&apos;s work orders based on the selected lot type change?
            </Typography>
          </div>
          <DialogActionsBar>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', padding: '8px' }}>
              <button
                className="btn btn-sm btn-primary"
                style={{ backgroundColor: '#0000b5', color: '#fff', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmYes}
              >
                Yes
              </button>
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{ backgroundColor: '#d7dbdd', color: 'black', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmNo}
              >
                No
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};
